import { useState } from 'react'
import { Label, XStack, YStack } from 'tamagui'
import { useToastController } from '@tamagui/toast'
import { Button } from '@components/Button'
import { HeroSelect } from '@components/HeroSelect'
import { SectionCard } from '@components/Profile/SectionCard'
import { useComponentTranslation } from '@hooks/useComponentTranslation'
import { useUserSettingsUpdate } from '@services/api/graphql/mutations/userSettingsUpdate'

const LOCALES = [
  { code: 'pt-BR', name: 'Português' },
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
]

interface ISettingsSectionProps {
  locale: string | null
}

export function SettingsSection({ locale }: ISettingsSectionProps) {
  const { t } = useComponentTranslation('Profile')
  const toast = useToastController()
  const { handleUpdateSettings, loading } = useUserSettingsUpdate()

  const current = LOCALES.find((l) => l.code === locale) ?? LOCALES[0]
  const [selectedCode, setSelectedCode] = useState(current.code)

  function handleSelect(name: string) {
    const found = LOCALES.find((l) => l.name.toLowerCase() === name)
    if (found) setSelectedCode(found.code)
  }

  async function handleSave() {
    const result = await handleUpdateSettings(selectedCode)
    if (result?.errors?.length) {
      toast.show(t('toast.errorTitle'), {
        message: result.errors.join('\n'),
        type: 'error',
      })
      return
    }

    toast.show(t('toast.successTitle'), { message: t('toast.successMessage') })
  }

  const selectedName =
    LOCALES.find((l) => l.code === selectedCode)?.name.toLowerCase() ?? ''

  return (
    <SectionCard title={t('settings.title')}>
      <YStack gap="$size.spacing-xs">
        <YStack maxWidth={320} gap="$size.spacing-xxxs">
          <Label
            color="$text-primary"
            ff="$mulishRegular"
            fontSize="$font-size-xxs"
            lineHeight={21}
            height="auto"
          >
            {t('settings.language')}
          </Label>
          <HeroSelect
            options={LOCALES.map((l) => ({ name: l.name }))}
            value={selectedName}
            onValueChange={handleSelect}
          />
        </YStack>

        <XStack justifyContent="flex-end">
          <Button variant="primary" loading={loading} onPress={handleSave}>
            {t('actions.save')}
          </Button>
        </XStack>
      </YStack>
    </SectionCard>
  )
}
